// src/components/settings/TagManagementSettings.js

import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, doc, addDoc, deleteDoc, updateDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { Plus, Trash2, Tag, Edit2, Check, X } from 'lucide-react';

const TagManagementSettings = ({ currentUserProfile }) => {
    const [tags, setTags] = useState([]);
    const [newTagLabel, setNewTagLabel] = useState('');
    const [newTagColor, setNewTagColor] = useState('#4B5563'); // Default to gray
    const [isLoading, setIsLoading] = useState(true);

    // --- EDITING STATE ---
    const [editingId, setEditingId] = useState(null);
    const [editLabel, setEditLabel] = useState('');
    const [editColor, setEditColor] = useState('');

    useEffect(() => {
        if (!currentUserProfile?.organizationId) {
            setIsLoading(false);
            return;
        }

        const tagsCollectionRef = collection(db, `organizations/${currentUserProfile.organizationId}/tags`);
        const q = query(tagsCollectionRef, orderBy('label'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setTags(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        }, (error) => {
            console.error("Error listening to tags:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();

    }, [currentUserProfile?.organizationId]);

    // --- CREATE ---
    const handleAddTag = async (e) => {
        e.preventDefault();
        if (!newTagLabel.trim() || !currentUserProfile?.organizationId) return;

        const label = newTagLabel.trim();
        if (tags.some(tag => tag.label.toLowerCase() === label.toLowerCase())) {
            alert('A tag with this name already exists.');
            return;
        }

        try {
            await addDoc(collection(db, `organizations/${currentUserProfile.organizationId}/tags`), {
                label: label,
                color: newTagColor,
            });
            setNewTagLabel('');
            setNewTagColor('#4B5563');
        } catch (error) {
            console.error("Error adding tag:", error);
        }
    };

    // --- DELETE ---
    const handleDeleteTag = async (tagId) => {
        if (!currentUserProfile?.organizationId) return;
        if (window.confirm('Are you sure you want to delete this tag? This will remove it from all clients it is currently assigned to.')) {
            try {
                await deleteDoc(doc(db, `organizations/${currentUserProfile.organizationId}/tags`, tagId));
            } catch (error) {
                console.error("Error deleting tag:", error);
            }
        }
    };

    // --- UPDATE (Start Editing) ---
    const startEditing = (tag) => {
        setEditingId(tag.id);
        setEditLabel(tag.label);
        setEditColor(tag.color || '#4B5563');
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditLabel('');
        setEditColor('');
    };

    // --- UPDATE (Save Changes) ---
    const saveEdit = async () => {
        if (!editLabel.trim() || !currentUserProfile?.organizationId) return;

        const cleanLabel = editLabel.trim();
        
        // Prevent duplicate names (exclude self)
        if (tags.some(t => t.label.toLowerCase() === cleanLabel.toLowerCase() && t.id !== editingId)) {
            alert('A tag with this name already exists.');
            return;
        }

        try {
            const tagRef = doc(db, `organizations/${currentUserProfile.organizationId}/tags`, editingId);
            await updateDoc(tagRef, {
                label: cleanLabel,
                color: editColor
            });
            setEditingId(null);
        } catch (error) {
            console.error("Error updating tag:", error);
            alert("Failed to update tag.");
        }
    };

    return (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold mb-2 text-gray-800">Client Tag Management</h2>
            <p className="text-sm text-gray-500 mb-6">Create and manage tags to segment your clients. Use the pencil icon to fix typos or change colors.</p>

            {/* ADD FORM (Updated to Stack on Mobile) */}
            <form onSubmit={handleAddTag} className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 p-4 border rounded-xl bg-gray-50 mb-6">
                <div className="flex-grow">
                    <label htmlFor="tag-label" className="block text-xs font-bold text-gray-500 uppercase mb-1">New Tag Label</label>
                    <input
                        id="tag-label"
                        type="text"
                        value={newTagLabel}
                        onChange={(e) => setNewTagLabel(e.target.value)}
                        placeholder="e.g., VIP, Residential"
                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                </div>
                <div>
                    <label htmlFor="tag-color" className="block text-xs font-bold text-gray-500 uppercase mb-1">Color</label>
                    <input
                        id="tag-color"
                        type="color"
                        value={newTagColor}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        className="w-full sm:w-16 h-[42px] p-1 border border-gray-300 rounded-lg cursor-pointer bg-white"
                    />
                </div>
                <button type="submit" className="flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 shadow-sm transition-colors">
                    <Plus size={18} className="mr-2" />
                    Add Tag
                </button>
            </form>

            {/* TAG LIST */}
            <div>
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-3">Existing Tags</h3>
                {isLoading ? <div className="p-4 text-gray-500 flex items-center gap-2"><div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div> Loading tags...</div> :
                    tags.length > 0 ? (
                        <ul className="space-y-2">
                            {tags.map(tag => (
                                <li key={tag.id} className="p-3 border rounded-lg hover:border-blue-300 transition-colors bg-white">
                                    {editingId === tag.id ? (
                                        // --- EDIT MODE (Mobile Optimized Wrap) ---
                                        <div className="flex flex-wrap items-center gap-3">
                                            <input 
                                                type="color" 
                                                value={editColor} 
                                                onChange={(e) => setEditColor(e.target.value)}
                                                className="w-10 h-10 rounded border p-0 cursor-pointer flex-shrink-0"
                                            />
                                            <input 
                                                type="text" 
                                                value={editLabel} 
                                                onChange={(e) => setEditLabel(e.target.value)}
                                                className="flex-grow min-w-[140px] p-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                autoFocus
                                            />
                                            <div className="flex gap-2 w-full sm:w-auto justify-end">
                                                <button onClick={saveEdit} className="p-2 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors" title="Save">
                                                    <Check size={18} />
                                                </button>
                                                <button onClick={cancelEditing} className="p-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors" title="Cancel">
                                                    <X size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        // --- VIEW MODE ---
                                        <div className="flex items-center justify-between">
                                            <span className="flex items-center gap-3">
                                                <Tag size={18} style={{ color: tag.color }} />
                                                <span className="font-bold text-gray-700 text-sm">{tag.label}</span>
                                            </span>
                                            
                                            <div className="flex items-center gap-2">
                                                <button 
                                                    onClick={() => startEditing(tag)} 
                                                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                                    title="Edit Tag"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteTag(tag.id)} 
                                                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                    title="Delete Tag"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-gray-500 text-sm">
                            No tags created yet. Add one above to get started.
                        </div>
                    )
                }
            </div>
        </div>
    );
};

export default TagManagementSettings;